import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { ConnectionStatus } from '../../hooks/usePeerConnection';

interface MultiplayerLobbyProps {
  status: ConnectionStatus;
  roomCode: string;
  error: string | null;
  isHost: boolean;
  playerName: string;
  remoteName?: string;
  onPlayerNameChange: (name: string) => void;
  onCreateRoom: () => void;
  onJoinRoom: (code: string) => void;
  onDisconnect: () => void;
  onStartGame: () => void;
  onBack: () => void;
}

export default function MultiplayerLobby({
  status,
  roomCode,
  error,
  isHost,
  playerName,
  remoteName,
  onPlayerNameChange,
  onCreateRoom,
  onJoinRoom,
  onDisconnect,
  onStartGame,
  onBack,
}: MultiplayerLobbyProps) {
  const [joinCode, setJoinCode] = useState('');
  const [copied, setCopied] = useState(false);

  // Reset copied state after delay
  useEffect(() => {
    if (copied) {
      const timer = setTimeout(() => setCopied(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [copied]);

  const copyRoomCode = () => {
    navigator.clipboard.writeText(roomCode);
    setCopied(true);
  };

  const handleJoinSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (joinCode.trim()) {
      onJoinRoom(joinCode.trim().toUpperCase());
    }
  };

  const renderDisconnectedState = () => (
    <motion.div
      className="kotch-lobby-options"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <div className="kotch-input-group">
        <label>Your Name</label>
        <input
          type="text"
          className="kotch-input"
          value={playerName}
          onChange={(e) => onPlayerNameChange(e.target.value)}
          placeholder="Enter your name"
        />
      </div>

      <div className="kotch-lobby-actions">
        <button
          className="kotch-button kotch-button-full"
          onClick={onCreateRoom}
          disabled={!playerName.trim()}
        >
          Create Room
        </button>

        <div className="kotch-lobby-divider">
          <span>or</span>
        </div>

        <form onSubmit={handleJoinSubmit} className="kotch-lobby-join-form">
          <div className="kotch-input-group">
            <label>Room Code</label>
            <input
              type="text"
              className="kotch-input kotch-input-code"
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              placeholder="KOTCH-XXXX"
              maxLength={10}
            />
          </div>
          <button
            type="submit"
            className="kotch-button kotch-button-full"
            disabled={!joinCode.trim() || !playerName.trim()}
          >
            Join Room
          </button>
        </form>
      </div>

      <button className="kotch-button kotch-button-secondary" onClick={onBack}>
        Back to Menu
      </button>
    </motion.div>
  );

  const renderConnectingState = () => (
    <motion.div
      className="kotch-lobby-connecting"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <div className="kotch-lobby-spinner" />
      <p>Connecting...</p>
      <button className="kotch-button kotch-button-secondary" onClick={onDisconnect}>
        Cancel
      </button>
    </motion.div>
  );

  const renderWaitingState = () => (
    <motion.div
      className="kotch-lobby-waiting"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <h3>Room Created!</h3>
      <p className="kotch-lobby-instruction">Share this code with your opponent:</p>

      <div className="kotch-room-code-display">
        <span className="kotch-room-code">{roomCode}</span>
        <button
          className="kotch-copy-button"
          onClick={copyRoomCode}
          title="Copy to clipboard"
        >
          {copied ? '✓' : '⧉'}
        </button>
      </div>

      <div className="kotch-lobby-waiting-indicator">
        <div className="kotch-lobby-pulse" />
        <p>Waiting for opponent to join...</p>
      </div>

      <button className="kotch-button kotch-button-secondary" onClick={onDisconnect}>
        Cancel
      </button>
    </motion.div>
  );

  const renderConnectedState = () => (
    <motion.div
      className="kotch-lobby-connected"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <h3>{remoteName || 'Opponent'} Connected!</h3>
      <div className="kotch-lobby-players">
        <div className="kotch-lobby-player">
          <span className="kotch-lobby-player-icon">⚔</span>
          <span>{playerName}</span>
          <span className="kotch-lobby-you">(You{isHost ? ' - Host' : ''})</span>
        </div>
        <div className="kotch-lobby-vs">vs</div>
        <div className="kotch-lobby-player">
          <span className="kotch-lobby-player-icon">⚔</span>
          <span>{remoteName || 'Opponent'}</span>
        </div>
      </div>

      {isHost ? (
        <button className="kotch-button kotch-button-full" onClick={onStartGame}>
          Start Match
        </button>
      ) : (
        <p className="kotch-lobby-waiting-text">Waiting for host to start...</p>
      )}

      <button className="kotch-button kotch-button-secondary" onClick={onDisconnect}>
        Leave Room
      </button>
    </motion.div>
  );

  const renderErrorState = () => (
    <motion.div
      className="kotch-lobby-error"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <h3>Connection Error</h3>
      <p className="kotch-error-message">{error}</p>
      <button className="kotch-button kotch-button-full" onClick={onDisconnect}>
        Try Again
      </button>
    </motion.div>
  );

  return (
    <motion.div
      className="kotch-lobby"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <h2>Online Multiplayer</h2>

      <AnimatePresence mode="wait">
        {status === 'disconnected' && renderDisconnectedState()}
        {status === 'connecting' && renderConnectingState()}
        {status === 'waiting' && renderWaitingState()}
        {status === 'connected' && renderConnectedState()}
        {status === 'error' && renderErrorState()}
      </AnimatePresence>
    </motion.div>
  );
}
